import {
  EligibilityEvent,
  EligibilityEventStatus,
  useEligibilityErrorFile,
  useEligibilityEvents,
  useEligibilityFile,
} from '../../../hooks/useEligibility';
import { useState } from 'react';
import SwitchBar from '../../../components/switch-bar';
import { TabOptions } from '../clients-constants';
import Header from '../../../components/common/global-top-bar';
import { useAllMinimalClients } from '../../../hooks/useClients';
import moment from 'moment';
import Spinner from '../../../components/common/spinner';
import { ROUTE_PATH } from '../../../routes/paths';

interface EligibilityEventFilters {
  clientId?: number;
  startDate?: string;
  endDate?: string;
  status?: string;
}

const EligibilityTool: React.FC = () => {
  const [apiFilters, setApiFilters] = useState<EligibilityEventFilters>({});
  const [status, setStatus] = useState<EligibilityEventStatus | undefined>(undefined);
  const { data, isLoading } = useEligibilityEvents(apiFilters);

  const sortEligibilityEvents = (events: EligibilityEvent[]) => {
    if (status === EligibilityEventStatus.COMPLETED) {
      return events.filter((event) => event.status === EligibilityEventStatus.COMPLETED);
    }
    if (status === EligibilityEventStatus.PROCESSING_FAILED) {
      return events.filter((event) => event.status === EligibilityEventStatus.PROCESSING_FAILED);
    }
    if (status === EligibilityEventStatus.IN_PROGRESS) {
      return events.filter((event) => event.status === EligibilityEventStatus.IN_PROGRESS);
    }
    return events;
  };

  const eligibilityEvents = data ? sortEligibilityEvents(data.data) : [];

  return (
    <div>
      <Header heading={'Eligibility Tool'} />
      <SwitchBar heading={TabOptions} position={1} />
      <Filters apiFilters={apiFilters} setApiFilters={setApiFilters} status={status} setStatus={setStatus} />
      <div>
        <div className="max-window-height-connections max-w-6xl overflow-y-auto px-7 pt-7 font-['Montserrat']">
          {isLoading && <Spinner />}
          {data?.count && data.count > 0 ? (
            <EligibilityEventTable data={eligibilityEvents} />
          ) : (
            <div>No results that match your current filters.</div>
          )}
        </div>
      </div>
    </div>
  );
};

interface FilterProps {
  apiFilters: EligibilityEventFilters;
  setApiFilters: (filters: EligibilityEventFilters) => void;
  status: EligibilityEventStatus | undefined;
  setStatus: React.Dispatch<React.SetStateAction<EligibilityEventStatus | undefined>>;
}

const Filters: React.FC<FilterProps> = ({ apiFilters, setApiFilters, status, setStatus }) => {
  const { data: clients } = useAllMinimalClients();

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setApiFilters({
      ...apiFilters,
      [name]: value,
    });
  };

  return (
    <div className="gray-border-line bg-gray-background-light flex h-10 w-full items-center justify-between border-b px-4">
      <div className="flex items-center space-x-2">
        <label className="whitespace-nowrap text-sm">Start Date:</label>
        <input
          type="date"
          name="startDate"
          value={apiFilters.startDate}
          onChange={handleDateChange}
          className="w-24 rounded border p-0.5 text-xs"
        />
        <label className="whitespace-nowrap text-sm">End Date:</label>
        <input
          type="date"
          name="endDate"
          value={apiFilters.endDate}
          onChange={handleDateChange}
          className="w-24 rounded border p-0.5 text-xs"
        />
        <label className="flex items-center whitespace-nowrap text-sm">
          Group:
          <select
            className="ml-1 h-6 w-60 rounded px-1 py-0.5 text-sm focus:outline-none"
            onChange={(e) => {
              const value = e.target.value;
              setApiFilters({
                ...apiFilters,
                clientId: value ? parseInt(value) : undefined,
              });
            }}
          >
            <option value="">All</option>
            {clients?.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex items-center">
        <button
          className={`whitespace-nowrap px-5 py-1 text-sm ${
            status === undefined ? 'bg-blue-primary text-white' : 'bg-gray-background-dark text-gray-dark'
          }`}
          onClick={() => setStatus(undefined)}
        >
          All
        </button>
        <button
          className={`whitespace-nowrap px-5 py-1 text-sm ${
            status === EligibilityEventStatus.COMPLETED
              ? 'bg-blue-primary text-white'
              : 'bg-gray-background-dark text-gray-dark'
          }`}
          onClick={() => setStatus(EligibilityEventStatus.COMPLETED)}
        >
          Completed
        </button>
        <button
          className={`whitespace-nowrap px-5 py-1 text-sm ${
            status === EligibilityEventStatus.PROCESSING_FAILED
              ? 'bg-blue-primary text-white'
              : 'bg-gray-background-dark text-gray-dark'
          }`}
          onClick={() => setStatus(EligibilityEventStatus.PROCESSING_FAILED)}
        >
          Failed
        </button>
        <button
          className={`whitespace-nowrap px-5 py-1 text-sm ${
            status === EligibilityEventStatus.IN_PROGRESS
              ? 'bg-blue-primary text-white'
              : 'bg-gray-background-dark text-gray-dark'
          }`}
          onClick={() => setStatus(EligibilityEventStatus.IN_PROGRESS)}
        >
          In Progress
        </button>
      </div>
    </div>
  );
};

interface EligibilityEventTableProps {
  data: EligibilityEvent[];
}

const EligibilityEventTable: React.FC<EligibilityEventTableProps> = ({ data }) => {
  const { downloadFile, isFetching: isFetchingFile } = useEligibilityFile();
  const { downloadErrorFile, isFetching: isFetchingErrorFile } = useEligibilityErrorFile();
  const timeZone = moment.tz(moment.tz.guess()).format('z');

  return (
    <table className="min-w-4xl border-collapse border border-gray-200">
      <thead>
        <tr className="bg-gray-100 text-left">
          <th className="w-24 p-3 text-sm font-bold text-gray-600">Dataset ID</th>
          <th className="w-64 p-3 text-sm font-bold text-gray-600">Clients</th>
          <th className="w-52 p-3 text-sm font-bold text-gray-600">Created At</th>
          <th className="w-52 p-3 text-sm font-bold text-gray-600">Completed At</th>
          <th className="w-64 p-3 text-sm font-bold text-gray-600">Packages</th>
          <th className="w-32 p-3 text-sm font-bold text-gray-600">Download</th>
        </tr>
      </thead>
      <tbody>
        {data?.map((event, index) => (
          <tr key={`${event.eligibility_dataset_id}-${index}`} className="border-b border-gray-200 hover:bg-gray-50">
            <td className="w-24 p-3 align-top text-sm">{event.eligibility_dataset_id}</td>
            <td className="w-64 p-3 align-top text-sm">
              <div className="flex flex-wrap gap-1">
                {event?.client_names?.map((name: string) => (
                  <span
                    key={name}
                    className="bg-blue-primary cursor-pointer rounded-md px-2 py-0.5 text-sm text-white"
                    onClick={() => {
                      localStorage.setItem('clientid', event.client_id.toString());
                      window.open(ROUTE_PATH.REPORTING, '_blank');
                    }}
                  >
                    {name}
                  </span>
                ))}
              </div>
            </td>
            <td className="w-52 whitespace-nowrap p-3 align-top text-sm">
              <div className="flex items-center gap-1">
                <p>{moment.utc(event.created_at).local().format('MMM DD, YYYY HH:mm:ss')}</p>
                <p className="text-xs font-bold text-neutral-600">({timeZone})</p>
              </div>
            </td>
            <td className="w-52 whitespace-nowrap p-3 align-top text-sm">
              <div className="flex items-center gap-1">
                {event.completed_at ? (
                  <>
                    <p>{moment.utc(event.completed_at).local().format('MMM DD, YYYY HH:mm:ss')}</p>
                    <p className="text-xs font-bold text-neutral-600">({timeZone})</p>
                  </>
                ) : event.status === EligibilityEventStatus.PROCESSING_FAILED ? (
                  <p className="text-sm font-medium text-red-600">Processing Failed</p>
                ) : event.status === EligibilityEventStatus.IN_PROGRESS ? (
                  <p className="text-sm font-medium text-yellow-600">Processing</p>
                ) : (
                  <p className="text-xs font-bold text-neutral-600">N/A</p>
                )}
              </div>
            </td>
            <td className="w-64 p-3 align-top text-sm">{event?.packages_names?.join(', ')}</td>
            <td className="w-32 p-3 align-top text-sm">
              <button
                className="bg-blue-primary rounded-full px-4 py-1 text-white"
                onClick={() => downloadFile(event.client_id, event.eligibility_dataset_id)}
                disabled={isFetchingFile}
              >
                {isFetchingFile ? 'Downloading...' : 'Download'}
              </button>
              {event.status === EligibilityEventStatus.PROCESSING_FAILED && (
                <button
                  className="bg-red mt-2 rounded-full px-5 py-1 text-white"
                  onClick={() => downloadErrorFile(event.eligibility_dataset_id)}
                  disabled={isFetchingErrorFile}
                >
                  {isFetchingErrorFile ? 'Downloading...' : 'Error File'}
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default EligibilityTool;
